import { LOCALE_REGION_MAPPING } from './live-agent-locale-utils';

interface ChatWidgetConfig {
  orgId: string;
  orgUrl: string;
  widgetId: string;
}

interface OmnichannelChatConfig {
  AUTHENTICATED: Record<string, ChatWidgetConfig>;
  UNAUTHENTICATED?: Record<string, ChatWidgetConfig>;
}

export const DEFAULT_PROD_CONFIG = {
  orgId: 'a8c967f2-d0c0-4bfc-a40f-6c7f603fb3b8',
  orgUrl: 'https://unqa8c967f2d0c04bfca40f6c7f603fb-crm.omnichannelengagementhub.com',
  widgetId: '655d901a-a75b-4167-87cb-bbc2acc82c7a',
} as const; // Authenticated Prod Config

export const DEFAULT_TEST_CONFIG = {
  orgId: '79c20b4b-277c-458b-8d64-8bedea472d15',
  orgUrl: 'https://unq79c20b4b277c458b8d648bedea472-crm.omnichannelengagementhub.com',
  widgetId: '8df379f8-0071-4174-9efc-344c47607a63',
} as const; // Authenticated Test Config

const AUTHENTICATED_PROD_CONFIGS: Record<string, ChatWidgetConfig> = {
  APGCEnglish: { ...DEFAULT_PROD_CONFIG, widgetId: '55913f57-1ee0-497b-8a6b-1d18fb3412c8' },
  APGCJapanese: { ...DEFAULT_PROD_CONFIG, widgetId: 'cdb97057-5b0e-4547-a4fd-de8b771b8c0b' },
  EMEADutch: { ...DEFAULT_PROD_CONFIG, widgetId: 'aab63e9a-8b3e-4adb-9d4e-35530c15a95b' },
  EMEAEnglish: { ...DEFAULT_PROD_CONFIG, widgetId: '25defb91-e37f-4174-b804-6325c9dad3db' },
  EMEAFrench: { ...DEFAULT_PROD_CONFIG, widgetId: '59c856bd-bea6-4a42-9e37-abe7c9b8d821' },
  EMEAGerman: { ...DEFAULT_PROD_CONFIG, widgetId: 'ee12e1d8-ca33-4172-9565-ff8badd77959' },
  EMEAItalian: { ...DEFAULT_PROD_CONFIG, widgetId: 'badebf28-d775-42d9-b8c2-ca18b3eacbe8' },
  EMEASpanish: { ...DEFAULT_PROD_CONFIG, widgetId: '8a6719d6-912a-4753-a333-a0b2533a6b50' },
  LATAMPortuguese: { ...DEFAULT_PROD_CONFIG, widgetId: '23358b2b-a42a-416c-bf63-c07c00b7da1c' },
  LATAMSpanish: { ...DEFAULT_PROD_CONFIG, widgetId: 'a9887ded-4a9b-4f45-88d0-e959d723bc1a' },
  NAFrench: { ...DEFAULT_PROD_CONFIG, widgetId: '59eb1e4c-134d-4218-be92-96b59234fd8d' },
  default: { ...DEFAULT_PROD_CONFIG, widgetId: '655d901a-a75b-4167-87cb-bbc2acc82c7a' },
};

const UNAUTHENTICATED_PROD_CONFIGS: Record<string, ChatWidgetConfig> = {
  APGCEnglish: { ...DEFAULT_PROD_CONFIG, widgetId: 'c859c0ed-cfba-47e4-abc0-59f128e06501' },
  APGCJapanese: { ...DEFAULT_PROD_CONFIG, widgetId: '4708ae87-20fe-47db-8c2d-a5f91516487a' },
  EMEADutch: { ...DEFAULT_PROD_CONFIG, widgetId: '1ab35765-24dd-4083-a1a9-bea2cef484b1' },
  EMEAEnglish: { ...DEFAULT_PROD_CONFIG, widgetId: 'a40217ec-bd1c-4392-a997-1a243aeadef8' },
  EMEAFrench: { ...DEFAULT_PROD_CONFIG, widgetId: '6b93962f-609c-4b5f-9aeb-f44a8ede44b5' },
  EMEAGerman: { ...DEFAULT_PROD_CONFIG, widgetId: '9eff49e0-e3a9-4b7e-9ee4-fdb03a2b867d' },
  EMEAItalian: { ...DEFAULT_PROD_CONFIG, widgetId: 'dfcec4d8-d84e-48d5-a985-405d67938898' },
  EMEASpanish: { ...DEFAULT_PROD_CONFIG, widgetId: '52533e24-1579-4300-8e8e-7f0eb5c599ac' },
  LATAMPortuguese: { ...DEFAULT_PROD_CONFIG, widgetId: '2b0aeb9f-e275-4562-8271-97237e763e6e' },
  LATAMSpanish: { ...DEFAULT_PROD_CONFIG, widgetId: 'af176f94-ece2-4949-9435-fe7af400cb87' },
  NAFrench: { ...DEFAULT_PROD_CONFIG, widgetId: 'ce1ace2e-b16c-4958-9f27-4d5e47d50112' },
  default: { ...DEFAULT_PROD_CONFIG, widgetId: '593f37e4-b30c-4ed0-852c-d0de6e6cd163' },
};

export const CHAT_WIDGET_TEST_CONFIG: OmnichannelChatConfig = {
  AUTHENTICATED: {
    default: {
      ...DEFAULT_TEST_CONFIG,
    },
  },
  UNAUTHENTICATED: {
    default: {
      ...DEFAULT_TEST_CONFIG,
    },
  },
};

export const CHAT_WIDGET_PROD_CONFIG: OmnichannelChatConfig = {
  AUTHENTICATED: AUTHENTICATED_PROD_CONFIGS,
  UNAUTHENTICATED: UNAUTHENTICATED_PROD_CONFIGS,
};

const getEnvChatConfig = (isProd: boolean): OmnichannelChatConfig => (isProd ? CHAT_WIDGET_PROD_CONFIG : CHAT_WIDGET_TEST_CONFIG);

const getRegionKey = (locale?: string): string => {
  if (!locale) return 'default';
  return LOCALE_REGION_MAPPING[locale.toLocaleLowerCase()] || 'default';
};

export const getOmnichannelConfig = (locale: string, isProd: boolean, isAuthenticated: boolean): ChatWidgetConfig => {
  const envConfig = getEnvChatConfig(isProd);
  const regionKey = getRegionKey(locale);
  const chatConfigGroup = isAuthenticated ? envConfig.AUTHENTICATED : envConfig.UNAUTHENTICATED;

  return chatConfigGroup?.[regionKey] || chatConfigGroup?.default || (isProd ? DEFAULT_PROD_CONFIG : DEFAULT_TEST_CONFIG);
};

export const cvSurveyConfig = {
  feedbackUrl: 'https://customervoice.microsoft.com/formapi/api/b4c546a4-7dac-46a6-a7dd-ed822a11efd3/users/788573e8-0668-4a50-a0b6-606ecdd77739/forms(\'pEbFtKx9pkan3e2CKhHv0-hzhXhoBlBKoLZgbs3XdzlURDJFRjMyNEZGRU1NTU9UWEdDSElQS1AzTi4u\')/responses',
  choiceQuestionId: 'r09f38746b4fd4f7f9c0c4447a4bba450',
  ratingQuestionId: 'rf212224ff1de45398404babf165333db',
  textQuestionId: 'rc0676a913e2b4cc39b0a9572d3a3b1dd',
};
